import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CreditPersistenceService } from '@/services/creditPersistenceService';

interface CreditState {
  balance: number;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

interface UseCreditsReturn extends CreditState {
  refetch: () => Promise<void>;
  updateBalance: (newBalance: number) => void;
  verifyBalance: () => Promise<void>;
}

export const useCredits = (): UseCreditsReturn => {
  const [state, setState] = useState<CreditState>({
    balance: 0,
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  const fetchCredits = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw new Error(`Session error: ${sessionError.message}`);
      }
      
      if (!session?.user) {
        setState(prev => ({ 
          ...prev, 
          balance: 0, 
          isLoading: false, 
          lastUpdated: new Date() 
        }));
        return;
      }

      // Use robust persistence service for fetching
      const result = await CreditPersistenceService.getUserBalance(session.user.id);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch credits');
      }

      const newBalance = result.newBalance || 0;
      
      setState(prev => ({
        ...prev,
        balance: newBalance,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      }));

      // Dispatch global event for other components
      window.dispatchEvent(new CustomEvent('creditBalanceUpdated', {
        detail: { newBalance }
      }));

    } catch (error) {
      console.error('Error fetching credits:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch credits',
        isLoading: false,
        lastUpdated: new Date(),
      }));
    }
  }, []);

  const updateBalance = useCallback((newBalance: number) => {
    setState(prev => ({
      ...prev,
      balance: newBalance,
      lastUpdated: new Date(),
    }));
  }, []);

  const verifyBalance = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const verificationResult = await CreditPersistenceService.verifyBalance(session.user.id);
      
      if (verificationResult.isConsistent) {
        updateBalance(verificationResult.actualBalance);
        console.log('✅ Credit balance verified and updated:', verificationResult.actualBalance);
      } else {
        console.warn('⚠️ Credit balance verification failed, refetching...');
        await fetchCredits();
      }
    } catch (error) {
      console.error('Balance verification failed:', error);
      await fetchCredits(); // Fallback to regular fetch
    }
  }, [fetchCredits, updateBalance]);

  // Initial fetch and event listeners
  useEffect(() => {
    fetchCredits();

    // Listen for credit balance updates from other components
    const handleCreditUpdate = (event: CustomEvent<{newBalance: number}>) => {
      if (event.detail && typeof event.detail.newBalance === 'number') {
        updateBalance(event.detail.newBalance);
      }
    };

    // Listen for credit data changes and trigger refetch
    const handleCreditDataChanged = (event: CustomEvent<{newBalance: number; timestamp: string}>) => {
      if (event.detail && typeof event.detail.newBalance === 'number') {
        updateBalance(event.detail.newBalance);
        // Also refetch to ensure we have the most up-to-date data
        setTimeout(fetchCredits, 100);
      }
    };

    // Listen for authentication state changes
    const handleAuthChange = () => {
      fetchCredits();
    };

    // Add event listeners
    window.addEventListener('creditBalanceUpdated', handleCreditUpdate as EventListener);
    window.addEventListener('creditDataChanged', handleCreditDataChanged as EventListener);
    window.addEventListener('authStateChanged', handleAuthChange);

    // Set up real-time subscription for credit changes
    let subscription: any = null;
    
    const setupRealtimeSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        subscription = supabase
          .channel('user_credits_realtime')
          .on('postgres_changes', 
            { 
              event: '*', 
              schema: 'public', 
              table: 'user_credits',
              filter: `user_id=eq.${session.user.id}` 
            }, 
            (payload: any) => {
              console.log('Real-time credit update:', payload);
              if (payload.new && typeof payload.new.balance === 'number') {
                updateBalance(payload.new.balance);
              }
            }
          )
          .subscribe();
      }
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      window.removeEventListener('creditBalanceUpdated', handleCreditUpdate as EventListener);
      window.removeEventListener('creditDataChanged', handleCreditDataChanged as EventListener);
      window.removeEventListener('authStateChanged', handleAuthChange);
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [fetchCredits, updateBalance]);

  return {
    balance: state.balance,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    refetch: fetchCredits,
    updateBalance,
    verifyBalance,
  };
}; 